import clsx from 'clsx';
import type { MouseEvent, ChangeEvent } from 'react';
import { useMemo, useState, useRef, useEffect } from 'react';
import type { IProduct } from '@recargas-dominicanas/core/types';
import { Icon, FieldError, Menu, SelectOption, Image } from '@recargas-dominicanas/core/components';
import { useProductStore } from '../../store/productStore';
import { style } from './product-select.module.css';

interface Props {
  className?: string;
  value?: IProduct;
  placeholder?: string;
  label?: string;
  error?: string;
  onChange: (value?: IProduct) => void;
  onBlur?: () => void;
}

export function ProductSelect(props: Props) {

  const {
    className,
    label,
    placeholder,
    error,
    value,
    onChange,
    onBlur
  } = props;

  const productStore = useProductStore();

  const ref = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState('');
  const [openPopup, setOpenPopup] = useState(false);
  const [products, setProducts] = useState(productStore.products);

  useEffect(() => {
    if (!value) {
      handleClear();
    }
  }, [!value]);

  useEffect(() => {
    if (value) {
      setInputValue(`${value.name} (${value.type})`);
      filterProducts(value.name);
    }
  }, [value?.id]);

  function handleClick() {
    setOpenPopup(state => !state);
    filterProducts(inputValue);
  }

  function handleClear(event?: MouseEvent<HTMLDivElement>) {
    event?.stopPropagation();
    setOpenPopup(false);
    setInputValue('');
    filterProducts('');
    onChange(undefined);
  }

  function handleBlur() {
    setOpenPopup(false);
    onBlur?.();
  }

  function handleOptionClick(value: IProduct) {
    setOpenPopup(false);
    onChange(value);
    filterProducts(label);
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    setInputValue(event.target.value);
    delaySearch(event.target.value);
  }
  
  const delaySearch = useMemo(() => {
    let timeout: NodeJS.Timeout;
    let lastValue: string | undefined;
    return (value?: string) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        if (value !== lastValue) {
          filterProducts(value);
          lastValue = value;
        }
      }, 100);
    };
  }, []);

  async function filterProducts(name = '') {
    setProducts(productStore.products.filter(product => 
      product.name
        .toLowerCase()
        .includes(name.toLowerCase()))
    );
  }

  return (
    <div className={clsx(style.container, className)}>
      {label && 
        <label className={style.label}>{label}</label>
      }
      <div 
        className={style.content}
        ref={ref} 
        onClick={handleClick}>
        {value?.image
          ? <Image className={style.image} src={value.image}/>
          : <Icon className={style.icon} icon='image'/>
        }
        <input 
          className={style.input} 
          value={inputValue}
          type='text'
          placeholder={placeholder}
          onChange={handleChange}/>
        {value
          ? <Icon className={style.rightIcon} icon='clear' onClick={handleClear}/>
          : <Icon className={style.rightIcon} icon='expand_more'/>
        }
      </div>
      <FieldError message={error}/>
      <Menu
        open={openPopup && products.length !== 0}
        anchor={ref} 
        top={8}
        autoWith={true}
        style={{ container: style.popup }}
        onClose={handleBlur}>
        {
          products?.map(product => (
            <SelectOption 
              style={style.option} 
              key={product.id} 
              value={product} 
              label={product.name}
              onClick={() => handleOptionClick(product)}>
              <Image className={style.optionImage} src={product.image}/>
              <div className={style.optionInfo}>
                <span className={style.optionName}>{product.name}</span>
                <span className={style.optionType}>{product.type}</span>
              </div>
            </SelectOption>
          ))
        }
      </Menu>
    </div>
  );
}
